import * as THREE from 'three';
import type { Anim, Appearance, Gender } from '@dn/shared';
import { CharacterModel } from './CharacterModel';
import { nameSprite, speechSprite } from '../effects/textures';

interface Snap { x: number; z: number; rotY: number; t: number }
interface Remote { model: CharacterModel; name: string; buf: Snap[]; anim: string; label: THREE.Sprite; bubble?: THREE.Sprite; bubbleUntil: number; ring?: THREE.Mesh }
const INTERP_DELAY = 120; // ms – nội suy giữa các gói để chuyển động mượt

/** Người chơi khác: nội suy vị trí phía client (client interpolation), nhãn tên, bong bóng chat/biểu cảm. */
export class RemotePlayers {
  private readonly map = new Map<string, Remote>();
  private readonly group = new THREE.Group();
  heightAt: (x: number, z: number) => number = () => 0;
  /** Đồng đội (cùng nhóm) – tên vàng + vòng sáng dưới chân */
  private party = new Set<string>();
  private readonly ringGeo = new THREE.RingGeometry(0.55, 0.7, 32);
  private readonly ringMat = new THREE.MeshBasicMaterial({ color: '#ffcf6a', transparent: true, opacity: 0.6, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  constructor(scene: THREE.Scene) { scene.add(this.group); }

  private makeLabel(r: { name: string }, id: string) {
    const ally = this.party.has(id);
    const l = nameSprite(r.name, ally ? 'Đồng đội' : undefined, ally ? '#ffd27a' : '#cfd8e3'); l.position.y = 2.15; return l;
  }
  private decorate(id: string, r: Remote) {
    r.label.removeFromParent(); r.label.material.map?.dispose(); r.label.material.dispose();
    r.label = this.makeLabel(r, id); r.model.root.add(r.label);
    const ally = this.party.has(id);
    if (ally && !r.ring) { r.ring = new THREE.Mesh(this.ringGeo, this.ringMat); r.ring.rotation.x = -Math.PI / 2; r.ring.position.y = 0.05; r.model.root.add(r.ring); }
    if (!ally && r.ring) { r.ring.removeFromParent(); r.ring = undefined; }
  }
  /** Cập nhật danh sách đồng đội */
  setParty(ids: string[]) {
    const next = new Set(ids); const changed = [...new Set([...next, ...this.party])].filter((id) => next.has(id) !== this.party.has(id));
    this.party = next; for (const id of changed) { const r = this.map.get(id); if (r) this.decorate(id, r); }
  }
  isAlly(id: string) { return this.party.has(id); }
  /** Người chơi khác đổi ngoại hình ⇒ dựng lại mô hình, giữ vị trí/nhãn */
  setAppearance(id: string, appearance: unknown) {
    const r = this.map.get(id); if (!r) return;
    const old = r.model; const model = new CharacterModel({ appearance: appearance as Appearance, gender: 'khac' as Gender });
    model.root.position.copy(old.root.position); model.root.rotation.y = old.root.rotation.y;
    for (const ch of [r.label, r.bubble, r.ring]) if (ch) model.root.add(ch); // chuyển nhãn/bong bóng/vòng sang mô hình mới
    old.dispose(); this.group.add(model.root); r.model = model;
  }
  ids() { return [...this.map.keys()]; }
  positionOf(id: string) { return this.map.get(id)?.model.root.position ?? null; }

  upsert(p: { characterId: string; name: string; appearance: unknown; x: number; z: number; rotY: number; anim?: string }) {
    if (this.map.has(p.characterId)) { this.push(p.characterId, p.x, p.z, p.rotY, p.anim ?? 'idle'); return; }
    const ap = p.appearance as Appearance;
    const model = new CharacterModel({ appearance: ap, gender: 'khac' as Gender });
    const label = this.makeLabel(p, p.characterId); model.root.add(label);
    model.root.position.set(p.x, this.heightAt(p.x, p.z), p.z); model.root.rotation.y = p.rotY;
    this.group.add(model.root);
    const r: Remote = { model, name: p.name, buf: [{ x: p.x, z: p.z, rotY: p.rotY, t: performance.now() }], anim: p.anim ?? 'idle', label, bubbleUntil: 0 };
    this.map.set(p.characterId, r);
    if (this.party.has(p.characterId)) this.decorate(p.characterId, r);
  }

  push(id: string, x: number, z: number, rotY: number, anim: string) {
    const r = this.map.get(id); if (!r) return;
    r.buf.push({ x, z, rotY, t: performance.now() }); if (r.buf.length > 20) r.buf.shift(); r.anim = anim;
  }

  say(id: string, text: string) {
    const r = this.map.get(id); if (!r) return;
    r.bubble?.removeFromParent(); r.bubble?.material.map?.dispose(); r.bubble?.material.dispose();
    /* bong bóng có khung, tự xuống dòng (≤3 dòng), ghi tên người nói – không còn chữ trần to tràn màn hình */
    const b = speechSprite(text.length > 120 ? text.slice(0, 119) + '…' : text, r.name, '#ffffff', 2.2); b.position.y = 2.45; r.model.root.add(b); r.bubble = b; r.bubbleUntil = performance.now() + 6000;
  }
  gesture(id: string, key: string) {
    const r = this.map.get(id); if (!r) return;
    if (key === 'nhay') r.model.setAnim('dance', 4); else if (key === 'co_vu') r.model.setAnim('cheer'); else r.model.setAnim(key === 'cui_dau' || key === 'chap_tay' ? 'bow' : 'wave');
  }

  remove(id: string) { const r = this.map.get(id); if (!r) return; r.ring?.removeFromParent(); r.model.dispose(); this.map.delete(id); }
  clear() { for (const id of [...this.map.keys()]) this.remove(id); }
  count() { return this.map.size; }

  update(dt: number, cam?: THREE.Vector3) {
    const renderT = performance.now() - INTERP_DELAY;
    for (const r of this.map.values()) {
      const b = r.buf; while (b.length >= 2 && b[1].t <= renderT) b.shift();
      let x = b[0].x, z = b[0].z, ry = b[0].rotY;
      if (b.length >= 2) { const k = Math.min(1, Math.max(0, (renderT - b[0].t) / (b[1].t - b[0].t))); x += (b[1].x - x) * k; z += (b[1].z - z) * k; let d = b[1].rotY - ry; d = Math.atan2(Math.sin(d), Math.cos(d)); ry += d * k; }
      r.model.root.position.set(x, this.heightAt(x, z), z); r.model.root.rotation.y = ry;
      r.model.setAnim(r.anim === 'run' || r.anim === 'walk' || r.anim === 'sit' || r.anim.startsWith('dance') ? r.anim as Anim : 'idle'); r.model.update(dt);
      if (cam) { /* người chơi khác đứng sát camera ⇒ mờ dần tên & bong bóng để không che màn hình */
        const d = Math.hypot(cam.x - x, cam.z - z); const o = Math.min(1, Math.max(0, (d - 1.6) / 2.2));
        for (const sp of [r.label, r.bubble]) if (sp) { sp.material.opacity = o; sp.visible = o > 0.02; }
      }
      if (r.bubble && performance.now() > r.bubbleUntil) { r.bubble.removeFromParent(); r.bubble.material.map?.dispose(); r.bubble.material.dispose(); r.bubble = undefined; }
    }
  }

  dispose() { this.clear(); this.group.removeFromParent(); this.ringGeo.dispose(); this.ringMat.dispose(); }
}
